import React from "react";

import PrecedentBlock from "../../../components/PrecedentBlock/PrecedentBlock";

/* Is this worth your time?
 *
 * ONE VERDICT, AT MOST THREE REASONS. The previous version showed four panels —
 * the RFP's shape, an evidence breakdown, prior bids, and things we cannot claim
 * — under a written paragraph, with captions explaining what had been read, why
 * matching was unavailable, and what counted as evidence. All of it was true and
 * none of it helped anyone decide. The screen explained the system rather than
 * the tender.
 *
 * The reader is a bid manager, often not technical, deciding whether to spend a
 * day on this. They will not read a paragraph to find out. So: a verdict, up to
 * three one-line reasons, what they would have to prove, and what happened last
 * time. Fewer lines is the correct answer whenever there is less to say — a
 * section with nothing in it does not appear at all.
 *
 * The honesty that used to live in captions now lives in the wording: "nothing
 * found on X" rather than a score, and never a classification code where a plain
 * phrase works.
 */

const VERDICTS = {
  "Worth a look": {
    tone: "text-[#1F7D38]",
    dot: "bg-[#2DAD4E]",
  },
  "Problems to resolve first": {
    tone: "text-[#7A5209]",
    dot: "bg-[#D9A441]",
  },
  "Probably not worth the time": {
    tone: "text-[#8A3F3F]",
    dot: "bg-[#B4655F]",
  },
  "Not enough information yet": {
    tone: "text-[#71717A]",
    dot: "bg-[#C4C4C8]",
  },
};

const Line = ({ children }) => (
  <li className="flex gap-2.5 py-[3px]">
    <span className="mt-[9px] h-[3px] w-[3px] flex-none rounded-full bg-[#C4C4C8]" />
    <span className="min-w-0 flex-1 text-[14px] leading-relaxed text-[#3F3F46]">
      {children}
    </span>
  </li>
);

const CopilotRecallCard = ({ message }) => {
  const d = message.data || {};
  const verdict = d.verdict;
  const style = VERDICTS[verdict] || VERDICTS["Not enough information yet"];

  if (message.state === "error") {
    return (
      <div className="rounded-2xl border border-[#B91C1C]/20 bg-[#B91C1C]/[0.03] px-4 py-3">
        <p className="text-[13.5px] text-[#B91C1C]">
          {message.error || "Couldn't check this project."}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-[#18101C]/[0.09] bg-white">
      <div className="px-5 pt-4">
        <h3 className="text-[15px] font-medium leading-snug text-[#18181B]">
          {message.projectLabel || "This project"}
        </h3>
        {d.subtitle && (
          <p className="mt-0.5 text-[12.5px] text-[#8A8A94]">{d.subtitle}</p>
        )}
      </div>

      {/* Still working. One line, not a skeleton of a layout that may not
          appear — the shape of the answer is not known until it arrives. */}
      {message.state === "loading" && (
        <div className="flex items-center gap-2 px-5 py-4">
          <span className="h-[6px] w-[6px] animate-pulse rounded-full bg-[#C4C4C8]" />
          <span className="text-[13.5px] text-[#8A8A94]">
            Checking this against what we know…
          </span>
        </div>
      )}

      {message.state === "ready" && (
        <div className="px-5 pb-5 pt-3">
          {/* ABOVE THE VERDICT, because it is a fact and the verdict is an
              opinion — the same order the tender page uses, from the same
              function, so one bid is never described two ways.

              It also owns this fact outright: the reasons below are forbidden
              from mentioning it. When they weren't, the card printed the
              restriction three times — as a reason copied from the quote, as a
              paraphrase, and as the quote. */}
          {d.restriction && (
            <div className="mb-3.5 rounded-lg border border-[#D9A441]/25 bg-[#D9A441]/[0.06] px-3.5 py-3">
              <p className="text-[14px] font-semibold leading-snug text-[#7A5209]">
                {d.restriction.headline}
              </p>
              {d.restriction.detail && (
                <p className="mt-0.5 text-[13px] leading-relaxed text-[#7A5209]/80">
                  {d.restriction.detail}
                </p>
              )}
              {d.restriction.quote && (
                <blockquote className="mt-2 border-l-2 border-[#7A5209]/25 pl-2.5">
                  <p className="text-[13px] italic leading-relaxed text-[#7A5209]/90">
                    &ldquo;{d.restriction.quote}&rdquo;
                  </p>
                  <cite className="mt-0.5 block text-[11.5px] not-italic text-[#7A5209]/60">
                    {d.restriction.quote_source}
                  </cite>
                </blockquote>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className={`h-[7px] w-[7px] flex-none rounded-full ${style.dot}`} />
            <p className={`text-[16px] font-semibold ${style.tone}`}>{verdict}</p>
          </div>

          {!!d.reasons?.length && (
            <ul className="mt-2.5">
              {d.reasons.map((r, i) => (
                <Line key={i}>{r}</Line>
              ))}
            </ul>
          )}

          {/* What happened last time: a fact from the reader's own record, not
              a judgement from the model. It used to be one more bullet among
              the reasons and read as one; it is the line most likely to change
              a decision, so it gets its own block - shared with the Tender
              Search card so a precedent is never styled two ways. */}
          <PrecedentBlock items={d.last_time} />

          {/* The most actionable thing on the card: what this bid would require
              that nothing on file supports. */}
          {!!d.youd_need?.length && (
            <div className="mt-3.5 border-t border-black/[0.06] pt-3">
              <p className="mb-1 text-[13px] font-medium text-[#52525B]">
                You&apos;d need
              </p>
              <ul>
                {d.youd_need.map((n, i) => (
                  <Line key={`n-${i}`}>{n}</Line>
                ))}
              </ul>
            </div>
          )}

          {/* `restriction` counts as something said. Without it in this test the
              card could print "there isn't enough on file" directly beneath the
              buyer's own sentence explaining why the bid is closed. */}
          {!d.reasons?.length &&
            !d.last_time?.length &&
            !d.youd_need?.length &&
            !d.restriction && (
              <p className="mt-2 text-[13.5px] leading-relaxed text-[#8A8A94]">
                There isn&apos;t enough on file about this project or your company
                to say anything useful yet.
              </p>
            )}
        </div>
      )}
    </div>
  );
};

/* Plain text for the copy button — a bid manager pastes this into an email. */
export const recallSummaryText = (message) => {
  const d = message.data || {};
  const out = [message.projectLabel || "Project"];
  if (d.subtitle) out.push(d.subtitle);
  // The quote goes into the email too — it is the line that settles the argument
  // with whoever asks why the bid was dropped.
  if (d.restriction) {
    out.push("", d.restriction.headline);
    if (d.restriction.quote) {
      out.push(`  "${d.restriction.quote}" — ${d.restriction.quote_source}`);
    }
  }
  if (d.verdict) out.push("", d.verdict);
  (d.reasons || []).forEach((r) => out.push(`  - ${r}`));
  (d.last_time || []).forEach((o) =>
    out.push(`  - ${o.outcome}: ${o.project}${o.why ? ` (${o.why})` : ""}`)
  );
  if ((d.youd_need || []).length) {
    out.push("", "You'd need:");
    d.youd_need.forEach((n) => out.push(`  - ${n}`));
  }
  return out.join("\n");
};

export default CopilotRecallCard;
